import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  Button,
  Checkbox,
  ListItemText,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
// utils
import { useServerRequest } from 'src/hooks/useServerRequest';
import { useLocales } from 'src/locales';
import { addInvitation } from 'src/redux/slices/dataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilteredData } from 'src/redux/selectors';

// Define the interface for the dialog's props
interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function InviteDialog({ open, onClose }: InviteUserDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const { makeRequest } = useServerRequest();
  const [isEmailValid, setIsEmailValid] = useState(true);
  const { t } = useLocales();
  const dispatch = useDispatch();
  const data = useSelector(selectFilteredData);

  // Function to generate a random token

  const onInvite = async (inviteData: { name: string; email: string; companies: string[]; role: string }) => {

    const response = await makeRequest(
      `/saveInvitation`,
      {
        method: 'POST',
        data: inviteData,
      },
      t('common.alert.inv')
    );

    if (response.success && response.data) {
      dispatch(addInvitation(response.data.invitation));
      onClose();
    }
  };

  const roles = [
    { id: 'employee', label: t('common.role.employee') },
    { id: 'analyst', label: t('common.role.analysis') },
  ];

  const [role, setRole] = useState(roles[0].id);

  const validateEmail = (emailToValidate: string) => {
    // Simple email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handleInvite = () => {
    if (isEmailValid) {
      onInvite({ name, email, companies: selectedCompanies, role });
    }
  };


  return (
    <Dialog open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()} disableEscapeKeyDown fullWidth maxWidth="sm">
      <DialogTitle>{t('m.t.inv')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t('common.input.name.label')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label={t('common.input.email.label')}
          value={email}
          onChange={handleEmailChange}
          margin="normal"
          type="email"
          required
          error={!isEmailValid}  // Show error if email is invalid
          helperText={!isEmailValid ? t('common.input.email.error1') : ""}
        />

        <Autocomplete
          sx={{ pt: 2 }}
          multiple
          fullWidth
          disableCloseOnSelect // Prevent closing after selecting an option
          value={selectedCompanies.map((id) => data.find((company) => String(company.id) === id) || null)} // Map selected company IDs to their corresponding objects
          options={data || []} // Provide companies list as options
          getOptionLabel={(option) => option?.name || ''} // Display company name, handle null safely
          onChange={(event, newValue) => setSelectedCompanies(newValue.map((company) => company?.id ? String(company.id) : ''))}
          renderOption={(props, option) => (
            <li {...props} key={option?.id}> {/* No need to manually handle `key`, it is in `props` */}
              <Checkbox checked={selectedCompanies.includes(String(option?.id))} />
              <ListItemText primary={option?.name} />
            </li>
          )}
          renderInput={(params) => <TextField {...params} label={t('common.input.comapnies.label')} />}
        />

        <FormControl fullWidth margin="normal" required>
          <Typography variant="body2" component="label" sx={{ mb: 1 }}>
            {t('roles.role')}
          </Typography>
          <ToggleButtonGroup
            value={role}
            exclusive
            onChange={(event, newRole) => setRole(newRole)}
            aria-label={t('common.input.role.label')}
            sx={{ display: 'flex', width: '100%' }} // Makes the group full-width
          >
            {roles.map((roleObj) => (
              <ToggleButton
                key={roleObj.id}
                value={roleObj.id}
                aria-label={roleObj.label}
                sx={{ flex: 1 }}
              >
                {roleObj.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </FormControl>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button onClick={handleInvite} variant="contained" disabled={!name || !email || selectedCompanies.length === 0 || !isEmailValid}>
          {t('common.button.invite2')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
